import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { EnhancedDashboardEmail, EmailReply } from '../types/api';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useEmailOperations } from '../hooks/useEmailOperations';
import { LexAIService } from '../services/api';

/**
 * Renders markdown-like text with support for **bold** and newlines
 * @param text - The text to render with basic markdown support
 * @returns React elements with formatted text
 */
const renderMarkdownText = (text: string): React.ReactNode => {
  if (!text) return null;

  // Split by newlines first
  const lines = text.split('\n');

  return lines.map((line, lineIndex) => {
    // Process **bold** markers in each line
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let keyIndex = 0;

    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);

      if (boldMatch && boldMatch.index !== undefined) {
        // Add text before the bold marker
        if (boldMatch.index > 0) {
          parts.push(remaining.substring(0, boldMatch.index));
        }
        // Add the bold text
        parts.push(
          <strong key={`bold-${lineIndex}-${keyIndex++}`}>
            {boldMatch[1]}
          </strong>
        );
        // Continue with remaining text
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
      } else {
        // No more bold markers, add the rest
        parts.push(remaining);
        break;
      }
    }

    // Return line with line break if not last line
    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {parts}
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

/**
 * Props interface for EmailDetails component
 */
interface EmailDetailsProps {
  /** The email object containing all email data from Microsoft Graph API */
  email: EnhancedDashboardEmail;
  /** Optional callback when user sends a reply */
  onSendReply?: (reply: EmailReply) => void;
  /** Optional callback when user closes the email details view */
  onClose?: () => void;
}

/**
 * Email Details Component - Advanced Email Viewer with AI Features
 *
 * @description
 * The EmailDetails component provides a comprehensive email viewing experience with AI-powered features.
 * It integrates directly with Microsoft Graph API for email operations and includes advanced functionality
 * like AI-generated summaries, smart reply generation, and secure HTML rendering.
 *
 * @features
 * - Real-time email content rendering from Microsoft Graph API
 * - AI-powered email summarization using Azure OpenAI
 * - Smart reply generation with context awareness
 * - Secure HTML content sanitization and rendering
 * - Attachment viewing and download capabilities
 * - Real-time reply composition with validation
 * - Australian timezone formatting for Sydney-based operations
 *
 * @api-integrations
 * - Microsoft Graph API for email content (Mail.Read, Mail.ReadWrite)
 * - Microsoft Graph API for attachments (Mail.Read)
 * - Azure OpenAI for AI summarization and reply generation
 * - LexAI backend for secure API orchestration
 *
 * @security-features
 * - HTML sanitization using DOMPurify with strict tag/attribute allowlists
 * - XSS prevention for email content rendering
 * - Secure attachment handling with size validation
 * - Input validation for reply content
 * - No execution of embedded scripts or active content
 *
 * @state-management
 * - Email content: Managed through props with real Microsoft Graph data
 * - AI Summary: Generated on-demand with loading states and caching
 * - Reply text: Local state with real-time validation and character limits
 * - Generation states: Loading indicators for AI operations (summary, reply generation, sending)
 * - Error handling: Comprehensive error states with user-friendly messages
 *
 * @performance
 * - Lazy loading of email content to reduce initial load time
 * - Efficient HTML parsing with memoized rendering
 * - Optimized AI API calls with request deduplication
 * - Attachment size validation before download operations
 */
const EmailDetails: React.FC<EmailDetailsProps> = ({
  email: initialEmail,
  onSendReply,
  onClose
}) => {
  // State for the email data - starts with initial data, gets updated with full details
  const [email, setEmail] = useState<EnhancedDashboardEmail>(initialEmail);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  const {
    aiSummary,
    isGeneratingSummary,
    generateSummary,
    replyText,
    setReplyText,
    isGeneratingReply,
    isSendingReply,
    generateDraftReply,
    handleSendReply
  } = useEmailOperations({ email, onSendReply });

  /**
   * Effect hook for handling email details initialization and content loading
   *
   * @description
   * Handles component initialization when email data changes.
   * If the email body is missing or appears to be placeholder text,
   * fetches the full email details from the API.
   */
  useEffect(() => {
    const fetchFullEmailContent = async () => {
      // Check if we need to fetch full email content
      // Body is missing, empty, or appears to be placeholder text
      const needsFullContent = !initialEmail.body ||
        initialEmail.body === '' ||
        initialEmail.body.startsWith('Email content for:') ||
        (initialEmail.body === initialEmail.bodyPreview && (!initialEmail.bodyPreview || initialEmail.bodyPreview.length < 100));

      if (needsFullContent && initialEmail.id) {
        setIsLoadingContent(true);
        try {
          const fullEmail = await LexAIService.getEmailDetails(initialEmail.id);
          if (fullEmail) {
            setEmail({
              ...initialEmail,
              ...fullEmail,
              // Preserve any existing fields that might not be in the API response
              sender: fullEmail.sender || initialEmail.sender,
              senderEmail: fullEmail.senderEmail || initialEmail.senderEmail,
            });
          }
        } catch (error) {
          console.error('Failed to fetch full email content:', error);
        } finally {
          setIsLoadingContent(false);
        }
      } else {
        // Use the initial email data as-is
        setEmail(initialEmail);
      }
    };

    fetchFullEmailContent();
  }, [initialEmail.id]);

  /**
   * Formats Microsoft Graph API date strings to Australian Sydney timezone
   *
   * @param {string} dateString - ISO date string from Microsoft Graph API (e.g., "2024-01-15T10:30:00Z")
   * @returns {string} Formatted date string in Australian format using Sydney timezone
   *
   * @description
   * Converts Microsoft Graph API date strings to user-friendly Australian format:
   * - Uses Sydney timezone (Australia/Sydney) for Prosper Finance operations
   * - Handles UTC dates from Microsoft Graph API
   * - Provides consistent Australian date formatting across the application
   * - Gracefully handles invalid dates by returning original string
   *
   * @example
   * ```typescript
   * // Graph API returns UTC date
   * const graphDate = "2024-01-15T10:30:00Z"; // 10:30 AM UTC
   * const formatted = formatAustralianTime(graphDate);
   * // Returns: "Mon, 15 Jan 2024, 9:30 pm" (Sydney time = UTC+11)
   *
   * // Handle invalid dates
   * const invalidDate = "invalid-date-string";
   * const result = formatAustralianTime(invalidDate);
   * // Returns: "invalid-date-string" (unchanged)
   * ```
   *
   * @timezone-handling
   * - Input: UTC dates from Microsoft Graph API
   * - Output: Sydney timezone (AEDT/AEST) for Australian business operations
   * - Daylight saving: Automatically handles AEDT (UTC+11) and AEST (UTC+10)
   */
  const formatAustralianTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Sydney',
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  /**
   * Securely renders Microsoft Graph API email content with comprehensive sanitization
   *
   * @param {string} body - Raw email body content from Microsoft Graph API Mail.Read endpoint
   * @param {string} bodyType - Content type from Microsoft Graph ('html', 'text', or undefined)
   * @returns {React.ReactElement} React element with safely rendered and sanitized content
   *
   * @description
   * Renders email content with enterprise-grade security and Microsoft Graph integration:
   * - Processes content from Microsoft Graph API Mail.Read and Mail.ReadWrite scopes
   * - Implements multi-layer security with DOMPurify sanitization
   * - Safe HTML parsing with html-react-parser for React compatibility
   * - Handles both HTML and plain text content from Graph API
   * - Comprehensive error handling with user-friendly fallbacks
   *
   * @security
   * - **XSS Prevention**: Removes all script, style, and event handler attributes
   * - **URL Sanitization**: Validates and sanitizes href/src URLs in links and images
   * - **Tag Filtering**: Strict allowlist of HTML tags and attributes
   * - **JavaScript Blocking**: Prevents execution of any embedded scripts
   * - **CSS Injection Prevention**: Removes style attributes and CSS that could compromise security
   *
   * @graph-api-integration
   * - **Content Source**: Processes body content from Microsoft Graph /me/messages endpoint
   * - **Content Types**: Handles both 'html' and 'text' body types from Graph API
   * - **Attachment Safety**: Works with secure attachment handling from Graph API
   * - **Encoding**: Handles UTF-8 encoding from Microsoft Graph responses
   *
   * @example
   * ```typescript
   * // Render HTML email from Graph API
   * const htmlBody = '<p>Hello <strong>world</strong>!</p><script>alert("xss")</script>';
   * const bodyType = 'html';
   * const safeElement = renderEmailBody(htmlBody, bodyType);
   * // Result: <div><p>Hello <strong>world</strong>!</p></div> (script removed)
   *
   * // Render plain text email
   * const textBody = 'Hello world!\nThis is a plain text email.';
   * const safeTextElement = renderEmailBody(textBody, 'text');
   * // Result: <div>Hello world!<br>This is a plain text email.</div>
   *
   * // Handle Graph API response
   * const graphEmail = {
   *   body: { content: '<div>Graph API content</div>', contentType: 'html' }
   * };
   * const rendered = renderEmailBody(graphEmail.body.content, graphEmail.body.contentType);
   * ```
   *
   * @error-handling
   * - Invalid HTML parsing falls back to error message
   * - Empty or null content shows loading state
   * - Sanitization failures display user-friendly error
   * - Network/API errors handled at higher component level
   */
  const renderEmailBody = (body: string, bodyType?: string) => {
    try {
      if (isLoadingContent) {
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            color: 'var(--obsidian-text-secondary)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid var(--obsidian-accent)',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Loading email content...
            </div>
          </div>
        );
      }
      if (!body) return 'Loading full email content...';

      // If it's HTML content, sanitize and render it properly
      if (bodyType === 'html' || body.includes('<')) {
        const cleanHtml = DOMPurify.sanitize(body, {
          ALLOWED_TAGS: [
            'p', 'br', 'div', 'span', 'a', 'img', 'strong', 'b', 'em', 'i', 'u',
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote',
            'table', 'thead', 'tbody', 'tr', 'td', 'th', 'pre', 'code', 'hr',
            'small', 'sub', 'sup', 'mark', 'del', 'ins', 'cite', 'abbr', 'time',
            'address', 'section', 'article', 'header', 'footer', 'main', 'aside',
            'figure', 'figcaption', 'details', 'summary'
          ],
          ALLOWED_ATTR: [
            'href', 'src', 'alt', 'title', 'class', 'width', 'height',
            'target', 'rel', 'type', 'name', 'value', 'checked', 'disabled',
            'readonly', 'placeholder', 'maxlength', 'minlength', 'required',
            'autofocus', 'multiple', 'accept', 'data-*', 'aria-*', 'role'
            // Note: 'style' intentionally removed to prevent light-theme inline styles
          ],
          ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
        });

        return (
          <div className="email-content-wrapper" style={{
            fontSize: '0.95rem',
            lineHeight: '1.7',
            color: 'var(--obsidian-text-primary)'
          }}>
            {/* Scoped styles to force dark theme on email content */}
            <style>{`
              .email-content-wrapper * {
                color: var(--obsidian-text-primary) !important;
                background-color: transparent !important;
                border-color: var(--obsidian-border) !important;
              }
              .email-content-wrapper a {
                color: var(--obsidian-accent) !important;
                text-decoration: underline;
              }
              .email-content-wrapper a:hover {
                color: #60a5fa !important;
              }
              .email-content-wrapper img {
                max-width: 100%;
                height: auto;
                border-radius: 8px;
                margin: 0.5rem 0;
              }
              .email-content-wrapper table {
                border-collapse: collapse;
                width: 100%;
                margin: 0.75rem 0;
              }
              .email-content-wrapper td,
              .email-content-wrapper th {
                padding: 0.75rem;
                border: 1px solid var(--obsidian-border) !important;
                text-align: left;
              }
              .email-content-wrapper blockquote {
                border-left: 3px solid var(--obsidian-accent);
                padding-left: 1rem;
                margin: 1rem 0;
                color: var(--obsidian-text-secondary) !important;
                font-style: italic;
              }
              .email-content-wrapper p {
                margin: 0.75rem 0;
              }
              .email-content-wrapper h1,
              .email-content-wrapper h2,
              .email-content-wrapper h3,
              .email-content-wrapper h4,
              .email-content-wrapper h5,
              .email-content-wrapper h6 {
                color: var(--obsidian-text-primary) !important;
                margin: 1rem 0 0.5rem 0;
                font-weight: 600;
              }
              .email-content-wrapper hr {
                border: none;
                border-top: 1px solid var(--obsidian-border);
                margin: 1.5rem 0;
              }
              .email-content-wrapper ul,
              .email-content-wrapper ol {
                padding-left: 1.5rem;
                margin: 0.75rem 0;
              }
              .email-content-wrapper li {
                margin: 0.25rem 0;
              }
              .email-content-wrapper pre,
              .email-content-wrapper code {
                background-color: var(--obsidian-bg) !important;
                padding: 0.25rem 0.5rem;
                border-radius: 4px;
                font-family: 'Consolas', 'Monaco', monospace;
                font-size: 0.9em;
              }
              .email-content-wrapper pre {
                padding: 1rem;
                overflow-x: auto;
              }
              .email-content-wrapper strong,
              .email-content-wrapper b {
                font-weight: 600;
                color: var(--obsidian-text-primary) !important;
              }
            `}</style>
            {parse(cleanHtml)}
          </div>
        );
      } else {
        // Plain text content
        return (
          <div style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            color: 'var(--obsidian-text-primary)',
            whiteSpace: 'pre-wrap'
          }}>
            {body}
          </div>
        );
      }
    } catch (error) {
      return (
        <div style={{
          color: 'var(--obsidian-text-secondary)',
          fontStyle: 'italic',
          padding: '1rem'
        }}>
          Error rendering email content. Please try refreshing.
        </div>
      );
    }
  };

  /**
   * Main content renderer for Microsoft Graph API email details with real-time synchronization
   *
   * @returns {React.ReactFragment} React fragment containing synchronized email content sections
   *
   * @description
   * Renders the complete email details interface with Microsoft Graph API integration:
   * - **Real-time AI Summary**: Azure OpenAI powered email summarization with loading states
   * - **Graph API Reply Composition**: Integrated reply interface using Mail.ReadWrite scope
   * - **Secure Attachment Handling**: Microsoft Graph attachment download with size validation
   * - **HTML Email Rendering**: Secure rendering of Graph API email body content
   * - **Live Synchronization**: Real-time updates from Microsoft Graph API endpoints
   *
   * @features
   * - **Conditional Rendering**: Dynamic display based on Graph API data availability
   * - **AI Integration**: Azure OpenAI summary generation with caching and error handling
   * - **Reply Composer**: Smart draft generation with Graph API send capabilities
   * - **Attachment Viewer**: Secure file display with Microsoft Graph download URLs
   * - **Security Rendering**: XSS-safe HTML rendering with DOMPurify sanitization
   *
   * @graph-api-endpoints
   * - **GET /me/messages/{id}**: Email content retrieval with Mail.Read scope
   * - **GET /me/messages/{id}/attachments**: Attachment metadata and download
   * - **POST /me/messages/{id}/reply**: Reply composition and sending
   * - **Azure OpenAI Integration**: AI summary generation via secure backend proxy
   *
   * @real-time-sync
   * - **Email Content**: Live synchronization with Microsoft Graph API
   * - **Attachment Status**: Real-time download progress and error handling
   * - **Reply Status**: Live send status with Graph API confirmation
   * - **AI Summary**: On-demand generation with caching for performance
   *
   * @example
   * ```typescript
   * // Component automatically renders Graph API email data
   * const emailFromGraph = {
   *   id: 'AAMkAGVmMDEzMTM4LTZmYWUtNDRjZC04MzY0LTNkNGY...',
   *   subject: 'Project Update',
   *   body: { content: '<div>Graph API HTML content</div>', contentType: 'html' },
   *   hasAttachments: true,
   *   attachments: [{ name: 'report.pdf', size: 2457600 }]
   * };
   *
   * // Renders: AI summary, reply composer, secure attachments, HTML body
   * const emailContent = renderContent();
   * ```
   *
   * @performance
   * - **Lazy Loading**: Email content loaded only when needed
   * - **Caching**: AI summaries cached to reduce API calls
   * - **Progressive Rendering**: Content sections load independently
   * - **Memory Management**: Large attachments handled efficiently
   */
  const renderContent = () => {
    return (
      <>
        {/* AI Summary */}
        {aiSummary && (
          <div style={{
            backgroundColor: 'var(--obsidian-sidebar-bg)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--obsidian-border)',
            margin: '1rem 0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, var(--obsidian-accent), #8b5cf6)',
                borderRadius: '50%',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span className="material-symbols-outlined" style={{ 
                  fontSize: '1.25rem',
                  color: 'white'
                }}>
                  auto_awesome
                </span>
              </div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>AI Summary</h3>
            </div>
            <div style={{
              fontSize: '1.1rem',
              lineHeight: '1.7',
              color: 'var(--obsidian-text-primary)'
            }}>
              {renderMarkdownText(aiSummary)}
            </div>
          </div>
        )}

        {/* Reply Composer */}
        {replyText && (
          <div style={{ margin: '1rem 0' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                borderRadius: '50%',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span className="material-symbols-outlined" style={{ 
                  fontSize: '1.25rem',
                  color: 'white'
                }}>
                  edit
                </span>
              </div>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>Draft Reply</h3>
            </div>

            <div style={{
              backgroundColor: 'var(--obsidian-sidebar-bg)',
              borderRadius: '12px',
              border: '1px solid var(--obsidian-border)',
              overflow: 'hidden'
            }}>
              {/* To/Subject fields */}
              <div style={{ padding: '1.25rem', borderBottom: '1px solid var(--obsidian-border)' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: '500',
                    color: 'var(--obsidian-text-secondary)',
                    display: 'block',
                    marginBottom: '0.5rem'
                  }}>
                    To
                  </label>
                  <div style={{ 
                    fontSize: '1rem',
                    color: 'var(--obsidian-text-primary)'
                  }}>{email.senderEmail}</div>
                </div>
                
                <div>
                  <label style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: '500',
                    color: 'var(--obsidian-text-secondary)',
                    display: 'block',
                    marginBottom: '0.5rem'
                  }}>
                    Subject
                  </label>
                  <div style={{ 
                    fontSize: '1rem',
                    color: 'var(--obsidian-text-primary)'
                  }}>
                    {email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`}
                  </div>
                </div>
              </div>

              {/* Reply textarea */}
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={isGeneratingReply ? "Generating reply..." : "Write your reply..."}
                disabled={isGeneratingReply || isSendingReply}
                style={{
                  width: '100%',
                  minHeight: '400px',
                  padding: '1.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'var(--obsidian-text-primary)',
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit'
                }}
              />

              {/* Action buttons */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                padding: '1.25rem',
                borderTop: '1px solid var(--obsidian-border)',
                backgroundColor: 'var(--obsidian-bg)'
              }}>
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || isSendingReply}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: replyText.trim() && !isSendingReply ? 'var(--obsidian-accent)' : 'var(--obsidian-border)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: replyText.trim() && !isSendingReply ? 'pointer' : 'not-allowed',
                    fontSize: '1rem',
                    fontWeight: '500',
                    opacity: replyText.trim() && !isSendingReply ? 1 : 0.6
                  }}
                >
                  {isSendingReply ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined" style={{ fontSize: '1.1rem' }}>
                        send
                      </span>
                      Send Reply
                    </>
                  )}
                </button>

                <button
                  onClick={() => generateDraftReply()}
                  disabled={isGeneratingReply}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'transparent',
                    color: 'var(--obsidian-accent)',
                    border: '1px solid var(--obsidian-accent)',
                    borderRadius: '8px',
                    cursor: isGeneratingReply ? 'not-allowed' : 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    opacity: isGeneratingReply ? 0.6 : 1
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>
                    refresh
                  </span>
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attachments */}
        {email.hasAttachments && email.attachments && email.attachments.length > 0 && (
          <div style={{
            backgroundColor: 'var(--obsidian-sidebar-bg)',
            padding: '1.25rem',
            borderRadius: '12px',
            border: '1px solid var(--obsidian-border)',
            margin: '1rem 0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                borderRadius: '50%',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span className="material-symbols-outlined" style={{ 
                  fontSize: '1.25rem',
                  color: 'white'
                }}>
                  attach_file
                </span>
              </div>
              <strong style={{ fontSize: '1.1rem' }}>
                {email.attachments.length} Attachment{email.attachments.length !== 1 ? 's' : ''}
              </strong>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {email.attachments.map((attachment, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem',
                    backgroundColor: 'var(--obsidian-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--obsidian-border)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span className="material-symbols-outlined" style={{ 
                      fontSize: '1.5rem',
                      color: 'var(--obsidian-accent)'
                    }}>
                      description
                    </span>
                    <div>
                      <div style={{ 
                        fontWeight: '500',
                        fontSize: '1rem',
                        marginBottom: '0.25rem'
                      }}>
                        {attachment.name}
                      </div>
                      <div style={{ 
                        fontSize: '0.85rem', 
                        color: 'var(--obsidian-text-secondary)'
                      }}>
                        {(attachment.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: 'var(--obsidian-accent)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>
                      download
                    </span>
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email Content */}
        <div style={{
          backgroundColor: 'var(--obsidian-sidebar-bg)',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid var(--obsidian-border)',
          margin: '1rem 0'
        }}>
          {renderEmailBody(email.body || email.bodyPreview || '', email.bodyType)}
        </div>
      </>
    );
  };

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: 'var(--obsidian-bg)',
      color: 'var(--obsidian-text-primary)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Fixed Header with proper spacing */}
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'var(--obsidian-bg)',
        borderBottom: '1px solid var(--obsidian-border)',
        padding: '1rem 2rem',
        zIndex: 10
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '1rem'
        }}>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              margin: '0 0 0.75rem 0',
              color: 'var(--obsidian-text-primary)',
              lineHeight: '1.3'
            }}>
              {email.subject}
            </h1>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '0.75rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--obsidian-accent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {email.sender?.charAt(0)?.toUpperCase() || email.senderEmail?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '1rem' }}>
                    {email.sender || email.senderEmail || 'Unknown Sender'}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--obsidian-text-secondary)' }}>
                    {email.senderEmail || ''}
                  </div>
                </div>
              </div>
              <div style={{
                fontSize: '0.9rem',
                color: 'var(--obsidian-text-secondary)',
                fontWeight: '500'
              }}>
                {formatAustralianTime(email.date)}
              </div>
            </div>
            
            {/* Status indicators */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {!email.isRead && (
                <span style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: 'var(--obsidian-accent)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  UNREAD
                </span>
              )}
              {email.isFlagged && (
                <span style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  FLAGGED
                </span>
              )}
              {email.importance === 'high' && (
                <span style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  HIGH PRIORITY
                </span>
              )}
              {email.hasAttachments && (
                <span style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: 'var(--obsidian-border)',
                  color: 'var(--obsidian-text-primary)',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  📎 {email.attachments?.length || 0}
                </span>
              )}
            </div>
          </div>

          {onClose && (
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--obsidian-border)',
                borderRadius: '8px',
                cursor: 'pointer',
                color: 'var(--obsidian-text-primary)',
                fontSize: '1.25rem'
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={generateSummary}
            disabled={isGeneratingSummary}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: 'transparent',
              color: 'var(--obsidian-accent)',
              border: '1px solid var(--obsidian-accent)',
              borderRadius: '6px',
              cursor: isGeneratingSummary ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              opacity: isGeneratingSummary ? 0.6 : 1
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>
              auto_awesome
            </span>
            {isGeneratingSummary ? 'Generating...' : 'AI Summary'}
          </button>

          <button
            onClick={() => generateDraftReply()}
            disabled={isGeneratingReply}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--obsidian-accent)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isGeneratingReply ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              opacity: isGeneratingReply ? 0.6 : 1
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>
              reply
            </span>
            {isGeneratingReply ? 'Generating...' : 'Reply'}
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '0 2rem 2rem'
      }}>
        {renderContent()}
      </div>

      {/* Global styles for animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

EmailDetails.propTypes = {
  email: PropTypes.shape({
    id: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
    sender: PropTypes.string.isRequired,
    senderEmail: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    body: PropTypes.string,
    bodyPreview: PropTypes.string,
    bodyType: PropTypes.string,
    hasAttachments: PropTypes.bool,
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        size: PropTypes.number.isRequired
      })
    ),
    isRead: PropTypes.bool,
    isFlagged: PropTypes.bool,
    importance: PropTypes.string
  }).isRequired,
  onSendReply: PropTypes.func,
  onClose: PropTypes.func
};

export default EmailDetails;